import React, { useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

interface LocaleRouterProps {
  children: React.ReactNode;
}

const LocaleRouter: React.FC<LocaleRouterProps> = ({ children }) => {
  const { locale } = useParams<{ locale?: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { i18n } = useTranslation();
  const [isInitialized, setIsInitialized] = React.useState(false);

  const supportedLocales = ['en', 'es', 'fr', 'de', 'pt', 'vi', 'sv', 'ro'];

  useEffect(() => {
    let shouldRender = true;
    
    // If we have a locale in the URL
    if (locale) {
      if (supportedLocales.includes(locale)) {
        if (i18n.language !== locale) {
          i18n.changeLanguage(locale);
        }
        shouldRender = true;
      } else {
        // The "locale" segment is not a valid locale — it may actually be a page name (e.g. /contact)
        const remainingPath = location.pathname.slice(`/${locale}`.length);
        const targetLocale = supportedLocales.includes(i18n.language) ? i18n.language : 'en';
        const targetPath = remainingPath
          ? `/${targetLocale}${remainingPath}`
          : `/${targetLocale}/${locale}`;
        navigate(targetPath, { replace: true });
        shouldRender = false;
      }
    } else {
      // If no locale in URL, detect and redirect
      const detectedLanguage = i18n.language;
      const targetLocale = supportedLocales.includes(detectedLanguage) ? detectedLanguage : 'en';
      
      // Only redirect if we're not already on the root path of the detected language
      if (location.pathname === '/' || !location.pathname.startsWith(`/${targetLocale}`)) {
        const newPath = location.pathname === '/' ? `/${targetLocale}/` : `/${targetLocale}${location.pathname}`;
        navigate(newPath, { replace: true });
        shouldRender = false;
      }
    }
    
    // Mark as initialized after processing
    setIsInitialized(true);
  }, [locale, i18n, navigate, location, supportedLocales]);

  // Don't render children until we've handled the locale routing
  // Always render if we have a valid locale
  if (locale && supportedLocales.includes(locale) && isInitialized) {
    return <>{children}</>;
  }

  // Show minimal loading state while initializing
  if (!isInitialized) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-white">Loading...</div>
      </div>
    );
  }

  // Invalid locale case - let the navigation redirect handle it
  return null;
};

export default LocaleRouter;